import React from 'react';
import { addDaysToLADateKey, formatLADateKeyDDMMMMYYYY } from '../../utils/laDateUtils';

/**
 * Create-mode date range picker for ParticipantBookingPanel, bounded to the stay period.
 * The native <input type="date"> always renders its value in the browser/OS locale format
 * (e.g. "08/09/2026"), which CSS cannot restyle — that text belongs to the browser, not the
 * DOM. So the input's own text is made transparent and a "24-July-2028" formatted <span> is
 * overlaid on top (pointer-events: none, so every click still lands on the real input below).
 *
 * Chrome only opens the native calendar on a direct click of its built-in icon glyph, not
 * anywhere else in the field — so clicking the formatted text or the empty space around it
 * did nothing, even though visually it's all one box. `openPicker` calls the input's own
 * `showPicker()` on every click (icon included — calling it while already open/opening is a
 * no-op, not a toggle), making the whole box one click target. Wrapped in try/catch since
 * `showPicker()` isn't universally supported (e.g. older Safari) — falls through to whatever
 * the browser's own default click behavior already was in that case.
 */
const openPicker = (e) => {
  try {
    e.currentTarget.showPicker?.();
  } catch {
    // Unsupported or blocked — native default click behavior still applies.
  }
};

const BookingDateRangePicker = ({ fromKey, toKey, stayInKey, stayOutKey, onFromChange, onToChange }) => (
  <div className="pbt-daterange-group">
    <div className="pbt-daterange-group__field">
      <label className="pbt-label">Check in</label>
      <div className="pbt-date-input-wrap">
        <input
          type="date"
          className="pbt-date-input"
          value={fromKey}
          min={stayInKey}
          max={stayOutKey ? addDaysToLADateKey(stayOutKey, -1) : undefined}
          onChange={(e) => onFromChange(e.target.value)}
          onClick={openPicker}
        />
        <span className="pbt-date-input__display" aria-hidden="true">
          {formatLADateKeyDDMMMMYYYY(fromKey)}
        </span>
      </div>
    </div>
    <div className="pbt-daterange-group__divider" aria-hidden="true">→</div>
    <div className="pbt-daterange-group__field">
      <label className="pbt-label">To</label>
      <div className="pbt-date-input-wrap">
        <input
          type="date"
          className="pbt-date-input"
          value={toKey}
          min={fromKey ? addDaysToLADateKey(fromKey, 1) : undefined}
          max={stayOutKey}
          onChange={(e) => onToChange(e.target.value)}
          onClick={openPicker}
        />
        <span className="pbt-date-input__display" aria-hidden="true">
          {formatLADateKeyDDMMMMYYYY(toKey)}
        </span>
      </div>
    </div>
  </div>
);

export default BookingDateRangePicker;
